import {React, Img, observer, ReactResponsiveCarousel, utils, Highlighter, useLocalStore, classNames, useEffect, useLayoutEffect, useAsObservableSource, Fragment, Popover2} from '@api'
import {ImageWrapperDefinition} from '@api/graphql'
import {Tooltip2} from '@api'

import {FieldRendererStore, useFieldRendererStore} from '@design-board/stores'
import {ThreeDRotation, Delete} from '@material-ui/icons'
import {LoadingIndicator, NoImagePlaceholder, useVipLocalStore} from '@modules'
import {useModalDetailSectionStore} from '@modules/CatalogItemDetails/CatalogItemSidebarDetails/Modal/ModalDetailSection'
import {StoresInstance} from '@stores'
import {PrimaryIconButton} from 'centric-ui-components'
import {observable, action, computed, toJS} from 'mobx'
import * as css from './ModalCardsField.css'
import {Icon, Slider} from '@blueprintjs/core'
import {EmersyaViewerStore, Emersya3dViewer} from '.'
import {CatalogFieldRenderer} from './CatalogFieldRenderer'
import {VipLocalStore} from '@modules'

interface MyProps {
  data: any
  index: number
}

export const ModalCardStoreContext = React.createContext<ModalCardStore>(null)
export const useModalCardStore = () => React.useContext(ModalCardStoreContext)

class ModalCardStore extends VipLocalStore {
  constructor(stores: StoresInstance,public props: MyProps, public store: FieldRendererStore) {
    super(stores)
  }

  @observable fullWidth = false
  @action toggleFullWidth = () => {
    if (!this.show3d) { // ignore when in 3d mode
      this.fullWidth = !this.fullWidth
    }
  }

  @observable show3d = false
  @action toggle3d = () => {
    this.show3d = !this.show3d
  }

  @observable.ref ref: HTMLDivElement
  @action setRef = r => this.ref = r

  @observable.ref store3d: EmersyaViewerStore
  @action onLoad3dStore = (store: EmersyaViewerStore) => this.store3d = store

  @computed get title() {
    const {props: {data}, es, store: {field: {titleField}}} = this
    return es.readField(data, titleField).valAsString
  }

  @computed get images() {
    return this.props.data?.images
  }

  @computed get isSearchResult() {
    const {store: {detailsStore: {isSearching, searchResults}, tabId}, props: {index}} = this
    return isSearching && searchResults.some(sr => sr.tab == tabId && sr.index == index && sr.type == 'titleField')
  }

  @computed get isCurrentSearchResult() {
    const {store: {detailsStore: {currentSearchResult}, isSearchResult, tabId}, props: {index}} = this
    return isSearchResult && currentSearchResult.tab == tabId && currentSearchResult.index == index
  }

  @computed get local3dImages() {
    const {catalog, store: {detailsStore: {data: rootData}}, props: {data}} = this
    return catalog.getOfflineItem({index: 'vlp-3d-images', _id: `${rootData?.exId}_${data?.exId}`})
  }

  @computed get images3d() {
    return [/* Uncomment once we can store the data on the real item instead of offline...(data?.images3d ?? []),*/ ...(this.local3dImages?.images3d ?? [])]
  }

  @action clear3dImages = () => this.catalog.upsertOfflineItem({index: 'vlp-3d-images', value: {...this.local3dImages, images3d: []}})

  @observable hover: boolean = false

  @action onMouseEnter = () => {
    this.hover = true
  }

  @action onMouseLeave = () => {
    this.hover = false
  }

  @action onDeleteData = () => {
    const {es, catalog, props: {data}, store: {catalogField, section, data: original, field, def, itemDetailsStore: {updateFieldData}}} = this,
      {readField} = es

    if (def?.type === "RefList") {
      const {val} = readField(original, field.field),
        updatedValue = val.map(v => catalog.idFor(v) === catalog.idFor(data) ? undefined : ({id: v.id, exId: v.exId, name: v.name, color: {id: v.id, exId: v.exId, name: v.name, images: v.images}})).filter(_.identity)

      updateFieldData(catalogField.esField ?? field.field, updatedValue, section)

    }
  }

  @computed get sectionFields() {
    const {store: {data, field: {fields}}} = this
    return _.isArray(fields) ? fields : fields(data, this.stores)
  }
}

export const ModalCard = observer<MyProps>(function ModalCard({data, index}) {
  const fieldRendererStore = useFieldRendererStore(),
    {field: {titleField, field: parentField, has3d}, value, isSearchResult, isCurrentSearchResult, detailsStore: {sidebar: {searchWords}, isSearching}} = fieldRendererStore,
    obsProps = useAsObservableSource({data, index}),
    store = useVipLocalStore((ws) => new ModalCardStore(ws, obsProps, fieldRendererStore)),
    {
      clear3dImages, images3d, images, title, setRef, ref, fullWidth, toggleFullWidth, show3d, toggle3d, onLoad3dStore, store3d, onMouseEnter,
      onMouseLeave, onDeleteData, catalog, sectionFields: fields
    } = store,
    scrollIntoViewOnExpand = useLayoutEffect(() => {
      if (fullWidth && ref) {
        setTimeout(() => fullWidth && ref.scrollIntoView({behavior: 'smooth', block: 'start'}), 50)
      }
    }, [fullWidth])

  // console.log(toJS({data, value, field: fieldRendererStore.field, parentField}, {recurseEverything: true}))

  return (
    <div className={css.card}
         ref={setRef}
         data-is-search-result={isSearching ? isSearchResult : undefined}
         data-is-current-search-result={isSearching ? isCurrentSearchResult : undefined}
         data-id={data._id ?? data.id ?? data.exId}
         data-fields={fields?.length ?? 0}
         data-full-width={fullWidth || show3d}
         onMouseEnter={onMouseEnter}
         onMouseLeave={onMouseLeave}
         data-show-3d={show3d}>
      {titleField &&
	   <div className={css.titlebar} onDoubleClick={toggleFullWidth}>
		   <span className={css.title}><Highlighter textToHighlight={title} searchWords={searchWords}/></span>
         {show3d && store3d?.menuButtons}
		   <span className={css.buttons}>
         {has3d && <Tooltip2 content="3D View" placement="bottom">
	         <PrimaryIconButton icon={<ThreeDRotation/>}
	                            className={css.button3d}
	                            active={show3d}
	                            onClick={toggle3d}
               // onClick={() => {
               //     emersya.selectedModel = data
               //     detailsStore.sidebar.tab = '3d'
               //   }}
	         />
         </Tooltip2>}
       </span>
	     <div
		     className={css.deleteIcon}
		     data-hover={store.hover}
	     >
		     <PrimaryIconButton icon={<Delete/>} onClick={onDeleteData} />
	     </div>
	   </div>}
      {show3d ?
       <div className={css.emersyaContainer}>
         <Emersya3dViewer className={css.emersya} selectedModel={data} onLoadStore={onLoad3dStore}/>
       </div>
              : <Fragment>
         {fields && <div className={css.fields} data-table="true">
			 <div className={css.cell} data-has-image={true} data-no-of-images={data?.images?.length ?? 0}>
				 <div className={css.images}>
                   {data?.images?.length ?
                    <ReactResponsiveCarousel className={css.carousel}
                              showArrows={false}
                              showThumbs={false}
                              useKeyboardArrows={true}
                    >
                      {data?.images?.map(
                        (image: ImageWrapperDefinition, i) => (
                          <div className={css.carouselItem} key={`${image.id}-${i}`}>
                            <Img src={catalog.imageUrlFor(image, {c8Size: 'image'})} loader={<LoadingIndicator type="placeholder"/>}/>
                          </div>
                        ))}
                    </ReactResponsiveCarousel>
                                         : <NoImagePlaceholder className={css.noImage}/>}

                   {false && show3d && <Tooltip2 content="3D View (inline)" placement="bottom">
					   <PrimaryIconButton icon={<Icon icon="cube"/>}
					                      className={classNames(css.button3d)}
					                      active={show3d}
					                      onClick={toggle3d}/>
				   </Tooltip2>}
				 </div>
			 </div>
           {fields.map((f, i) => {
             const field = `${parentField}[${index}].${f.field}`
             return (
               <div className={css.cell} key={i} data-field={field}>
                 <CatalogFieldRenderer key={i} align="modal" field={{...f, field}}/>
               </div>)
           })}
		 </div>}
       </Fragment>
      }
      {has3d /*&& (localData?.images3d || show3d)*/ &&
	   <div className={css.images3d}>
		   <label>3D Captures ({images3d?.length ?? 0})
             {images3d?.length ? <a className={css.clearImages3d} onClick={clear3dImages}>Clear Images</a> : null}
		   </label>
		   <div className={css.list}>
             {images3d?.map((img, i) =>
               <Popover2 interactionKind="hover"
                        disabled
                        key={i}
                        hoverOpenDelay={500}
                        content={
                          <img src={img} className={css.popupImage}/>
                        }>
                 <img src={img} onClick={() => utils.openDataImgInNewTab(img)}/>
               </Popover2>
             )}
		   </div>
	   </div>}
    </div>)
})

class ModalCardsStore {
  constructor(public store: FieldRendererStore) {

  }

  @observable columns = 3
  @observable maxColumns = 5
  @observable showingTabIcons = false

  @computed get style(): any {
    const {columns, maxColumns} = this
    return {
      //'--columns': columns == maxColumns ? 'auto-fit' : columns
    }
  }

  @action changeColumns = v => this.columns = v

  sliderLabelRenderer = (value: number) => {
    return value == this.maxColumns ? 'Auto' : value.toString()
  }
}

export const ModalCardsField = observer(function ModalCardsField() {
  const store = useFieldRendererStore(),
    modalSectionStore = useModalDetailSectionStore(),
    {value} = store,
    myStore = useLocalStore(() => new ModalCardsStore(store)),
    {columns, maxColumns, changeColumns, style, sliderLabelRenderer} = myStore,
    section = modalSectionStore?.section ?? store?.section

  // useEffect(() => {
  //   if (selectedDetailTab?.id == tabId) {
  //     modal.extraTabHeader = (
  //       <Observer>
  //         {() => {
  //           const {detailsStore: {modal: {nestedCardsViewMode, onSetNestedCardsViewMode}}} = store
  //           return (<div className={css.toolbarButtons}>
  //             <Tooltip2 placement='bottom' title={strings.gridView}>
  //               <PrimaryIconButton icon={<Icon icon='grid-view'/>} active={nestedCardsViewMode == 'grid'} onClick={onSetNestedCardsViewMode('grid')}/>
  //             </Tooltip2>
  //             <Tooltip2 placement='bottom' title={strings.listView}>
  //               <PrimaryIconButton icon={<Icon icon='list'/>} active={nestedCardsViewMode == 'list'} onClick={onSetNestedCardsViewMode('list')}/>
  //             </Tooltip2>
  //           </div>)
  //         }}
  //       </Observer>
  //     )
  //     myStore.showingTabIcons = true
  //   }
  //   else {
  //     if (myStore.showingTabIcons) {
  //       modal.extraTabHeader = false
  //       myStore.showingTabIcons = false
  //     }
  //   }
  // }, [selectedDetailTab])

  let cards = !value?.val ? [] : _.isArray(value?.val) ? value?.val : [value.val]

  // TODO - temp bugfix 5/19/21, take first of each array server gives us
  cards = cards.map(c => _.isArray(c) ? _.first(c) : c)

  // console.log(toJS({cards, value, section}, {recurseEverything: true}))

  return (
    <div className={css.root}
         style={{'--columns': 3} as any}
         data-item-count={cards.length}
         data-view-mode={store.detailsStore.modal?.nestedCardsViewMode}>
      {false && !section && <div className={css.toolbar}>
		  <Tooltip2 content="Columns">
			  <div className={css.slider}>
                {/*<label>Columns</label>*/}
				  <Slider max={maxColumns} min={1} value={columns} onChange={changeColumns} labelRenderer={sliderLabelRenderer}/>
			  </div>
		  </Tooltip2>
	  </div>}

      {_.isEmpty(cards)
       ? <span className={css.noItems}>No items</span>
       : <div className={css.cards}>
         {cards.map((v, i) => <ModalCard key={i} data={v} index={i}/>)}
         {_.range(0, columns - (cards.length % columns || columns)).map(i => <div className={css.gridFiller} key={i}/>)}
       </div>}
    </div>)
})


// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/FieldRenderers/ModalCardsField.tsx